import React, { Component } from "react";
import axios from "axios";
import PrismDataGrid from "../../../shared/PrismDataGrid";
import UserTypeahead from './UserTypeahead';
import { PrismLoading, PrismButton, PrismSelect, PrismInputText, PrismBanner, PrismTabs, PrismTab } from "@prism-ui/react";
import { toast } from "../../../Toaster/toast";
import ExcelJS from 'exceljs';
import clearLogoSrc from '../../../../assets/images/clear-logo.svg';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faIdBadge, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  getAllUsers,
  getProgramUsers,
  getSimulationCandidates,
  saveUser,
  removeUser,
} from "../../../../services/users";
import "./ManageUsers.css";
import Unauthorized from "../../../Unauthorized";
import PermissionLevel from "../../../../data/PermissionLevel";
import ConfirmDeleteButton from "../../../ConfirmDeleteButton";
import AdminPane from '../../AdminPane';

/*
 * prism-tabs requires each <prism-tab> to carry an `id` matching its Tab object's `controls`
 * value. Without it the first panel renders correctly and then every panel disappears
 * permanently on the first tab switch. See client/design-lab/prism-tabs-probe.html.
 */
const panelId = (id) => `manage-users-panel-${id}`;

const IMPERSONATION_TAB = {
  id: 'impersonation',
  title: 'Impersonation',
  controls: panelId('impersonation'),
};

//TODO: this is also used in the Submissions.js, abstract it out
class ManageUsers extends Component {
  filteredData = []; // Track filtered data for export
  successTimeoutId = null; // Track timeout for auto-clearing success messages

  state = {
    validPermissionLevel: 90,
    users: [],
    allUsers: [],
    chapters: [],
    roles: [],
    showUsersDropdown: false,
    canUpdate: this.props.currentUser.Role.permission_level >= PermissionLevel.Officer,
    fields: [],
    selUser: null,
    simUser: null,
    selRole: -1,
    selChapter: -1,
    saving: false,
    loading: true,
    officerLoginAsEnabled: false,
    nameFilter: "",
    chapterFilter: "",
    roleFilter: "",
    filteredUsers: [],
    simCandidates: [],
    activeTab: 'roles',
  };

  tabsRef = React.createRef();

  /**
   * The tab set is dynamic: Impersonation only exists when the site setting allows it, and
   * that setting arrives from an async fetch after first paint. `tabs` is an array, so it has
   * to be assigned as a DOM property — an attribute would stringify it — and it is reassigned
   * whenever the set changes rather than mutated, since prism-tabs reads the whole array.
   */
  syncTabs = () => {
    const el = this.tabsRef.current;
    if (!el) return;
    const tabs = this.getTabs();
    const serialised = tabs.map((t) => t.id).join('|');
    if (serialised === this.lastTabSignature) return;
    this.lastTabSignature = serialised;
    el.tabs = tabs;
  };

  getTabs = () => {
    const tabs = [];
    // Order must match the DOM order of the <prism-tab> children below.
    tabs.push({ id: 'roles', title: 'User Roles', controls: panelId('roles') });
    tabs.push({ id: 'add', title: 'Add a User Role', controls: panelId('add') });
    if (this.canShowLoginAs()) tabs.push(IMPERSONATION_TAB);
    return tabs.map((tab) => ({ ...tab, active: tab.id === this.state.activeTab }));
  };

  canShowLoginAs = () => {
    const level = this.props.currentUser.Role?.permission_level;
    const hasOfficerPermission = level >= PermissionLevel.Officer;
    const isAdmin = level >= PermissionLevel.Admin;
    return hasOfficerPermission && (isAdmin || this.state.officerLoginAsEnabled);
  };

  // Columns are built at render time rather than stored in state. The previous version
  // built them once in componentDidMount, so they closed over the chapters, roles and
  // canUpdate values captured at that moment.
  getColumns = () => {
    const { chapters, roles, canUpdate } = this.state;

    return [
      {
        id: "User.name",
        header: "Name",
        sortable: true,
        accessor: (row) => row.User?.name ?? "",
        width: "450px",
      },
      {
        id: "Chapter.id",
        header: "Chapter",
        sortable: true,
        accessor: (row) =>
          chapters.find((c) => Number(c.id) === Number(row.Chapter?.id))?.location || "",
        width: "280px",
        editable: canUpdate
          ? {
              type: "select",
              options: chapters.map((c) => ({
                label: c.location,
                value: String(c.id),
              })),
              onSave: (row, value) => this.handleFieldSave(row, "Chapter.id", value),
            }
          : undefined,
      },
      {
        id: "Role.id",
        header: "Role",
        sortable: true,
        accessor: (row) =>
          roles.find((r) => Number(r.id) === Number(row.Role?.id))?.name || "",
        width: "240px",
        editable: canUpdate
          ? {
              type: "select",
              options: roles.map((r) => ({ label: r.name, value: String(r.id) })),
              onSave: (row, value) => this.handleFieldSave(row, "Role.id", value),
            }
          : undefined,
      },
      {
        id: "Actions",
        header: "",
        width: "100px",
        align: "center",
        cell: (row) => (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <ConfirmDeleteButton
              confirmLabel="Delete"
              disabled={!this.state.canUpdate}
              onDelete={() => this.deleteUser(row)}
            />
          </div>
        ),
      },
    ];
  };

  // The grid's select editor reports the chosen value; the old table reported both the
  // old and new value. The previous value is read off the row instead.
  handleFieldSave = (row, dataField, newValue) => {
    const oldValue =
      dataField === "Chapter.id" ? row.Chapter?.id : row.Role?.id;
    return this.handleChange(oldValue, newValue, row, { dataField });
  };

  componentDidMount = async () => {
    this.syncTabs();
    let programUsers = await getProgramUsers();
    let allUsers = await getAllUsers();
    // The impersonation picker gets its own list. /api/users is everyone, which
    // includes accounts impersonation silently no-ops on.
    let simCandidates = [];
    try {
      const candidates = await getSimulationCandidates();
      simCandidates = Array.isArray(candidates) ? candidates : [];
    } catch (error) {
      console.error('Error fetching impersonation candidates:', error);
    }
    let chapters = await axios.get('/api/chapters').then(r => r.data);
    let roles = await axios.get('/api/roles').then(r => r.data);
    
    // Initialize filtered data
    this.filteredData = programUsers;
    
    // Fetch officer login as setting
    let officerLoginAsEnabled = false;
    try {
      const settingsResponse = await axios.get('/api/adminSettings/settings');
      const loginAsSetting = settingsResponse.data.find(s => s.setting_key === 'officer_login_as_enabled');
      officerLoginAsEnabled = loginAsSetting?.setting_value === 'true';
    } catch (error) {
      console.error('Error fetching officer login as setting:', error);
    }
      
    this.setState({
      allUsers,
      simCandidates,
      users: programUsers,
      chapters: chapters,
      roles: roles,
      officerLoginAsEnabled,
      loading: false,
    });
  };

  componentDidUpdate = () => {
    this.syncTabs();
  };

  addUser = async () => {
    if (!this.validateUser()) {
      return; // Stop here if validation fails
    }
    
    this.setState({ saving: true });

    let user = {
      user_id: this.state.selUser[0].id,
      chapter_id: this.state.selChapter,
      role_id: this.state.selRole,
      year: new Date().getFullYear(),
    };

    try {
      await this.saveUser(user);
      let userList = await getProgramUsers();

      const userName = this.state.selUser[0].name;
      const chapterName = this.state.chapters.find(c => Number(c.id) === Number(this.state.selChapter))?.location;
      const roleName = this.state.roles.find(r => Number(r.id) === Number(this.state.selRole))?.name;

      // Additional safety check - shouldn't happen with proper validation
      if (!userName || !chapterName || !roleName) {
        throw new Error('Missing required field data');
      }

      this.setState({
        selUser: null,
        selChapter: -1,
        selRole: -1,
        users: userList,
        saving: false,
      });

      toast.success(`Successfully added ${userName} as ${roleName} in ${chapterName}.`);
    } catch (err) {
      console.error('Error adding user:', err);
      toast.error("Failed to add user. Please try again.");
      this.setState({ saving: false });
    }
  };

  saveUser = async (user, meta) => {
    await saveUser({ ...user, ...meta });
    
    // If we just updated the current user's info, refresh it
    if (user.user_id === this.props.currentUser.User.id) {
      await this.props.refreshCurrentUser?.();
    }
  };

  deleteUser = async (user) => {
    if (!this.state.canUpdate) {
      window.alert("You do not have permissions to edit");
      return;
    }

    try {
      await removeUser(user);

      let users = [...this.state.users];
      let usersNew = users.filter((u) => u.user_id !== user.user_id);
      
      this.setState({ users: usersNew });

      toast.success(`Successfully removed ${user.User.name} from the program.`);
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error("Failed to remove user. Please try again.");
    }
  };

  componentWillUnmount = () => {
    // Clear any pending timeouts to prevent memory leaks
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
    }
  };

  validateUser = () => {
    const missingFields = [];

    if (!this.state.selUser || !this.state.selUser.length) {
      missingFields.push("user");
    }
    if (!this.state.selChapter || this.state.selChapter === -1 || this.state.selChapter === "-1") {
      missingFields.push("chapter");
    }
    if (!this.state.selRole || this.state.selRole === -1 || this.state.selRole === "-1") {
      missingFields.push("role");
    }

    if (missingFields.length > 0) {
      let errorMessage;
      
      if (missingFields.length === 1) {
        const field = missingFields[0];
        if (field === "user") {
          errorMessage = "You must select a user that hasn't already been assigned a role.";
        } else if (field === "chapter") {
          errorMessage = "You must select a chapter.";
        } else {
          errorMessage = "You must select a role.";
        }
      } else if (missingFields.length === 2) {
        if (missingFields.includes("user")) {
          const otherField = missingFields.find(f => f !== "user");
          errorMessage = `You must select a user and ${otherField === "chapter" ? "a chapter" : "a role"}.`;
        } else {
          errorMessage = "You must select a chapter and a role.";
        }
      } else {
        errorMessage = "You must select a user, chapter, and role.";
      }

      toast.error(errorMessage);
      return false;
    }

    return true;
  };

  exportUsers = () => {
    if (
      !this.state.users ||
      !this.state.users.length ||
      !this.state.canUpdate
    ) {
      return;
    }

    // Get our list of users and define our header
    const users = this.state.users;
    let csv = "NTID/Email Address, Role 1, Role 2, Role 3,";

    // Iterate over users and generate rows as strings
    const checkedSet = new Set();
    users.forEach((user) => {
      if (!checkedSet.has(user.id)) {
        csv += `\n${user.User.email},${user.Role.name},${""},${""},`;
        checkedSet.add(user.id);
      }
    });

    // Generate a download with formatted date and time (YYYY-MM-DD_HHMMSS)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const fileName = `clear_uar_${year}-${month}-${day}_${hours}${minutes}${seconds}.csv`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.setAttribute("style", "display: none");
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  handleFilteredRowsChange = (rows) => {
    this.filteredData = rows;
    // Re-render so the "Export Current View" button's disabled state tracks the filters.
    this.setState({ filteredUsers: rows });
  };

  // Name, Chapter and Role filters are applied in React rather than inside the grid:
  // prism-table renders its own filter UI and cannot host a custom header filter.
  getVisibleUsers = () => {
    const { users, nameFilter, chapterFilter, roleFilter } = this.state;
    const needle = nameFilter.trim().toLowerCase();
    return users.filter((u) => {
      if (needle && !(u.User?.name ?? "").toLowerCase().includes(needle)) return false;
      if (chapterFilter && String(u.Chapter?.id) !== String(chapterFilter)) return false;
      if (roleFilter && String(u.Role?.id) !== String(roleFilter)) return false;
      return true;
    });
  };

  // The grid has no header filters here, so clearing the React-side filters is enough;
  // the grid keeps its sort.
  handleClearAllFilters = (e) => {
    this.setState({
      nameFilter: '',
      chapterFilter: '',
      roleFilter: '',
    });
    // Reset filtered data to full dataset
    this.filteredData = this.state.users;
    e.currentTarget.blur();
  };

  exportCurrentView = async () => {
    if (!this.state.canUpdate || !this.filteredData || !this.filteredData.length) {
      return;
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'CLEAR Portal';
    workbook.created = new Date();
    workbook.subject = 'User Roles - Current View';

    const worksheet = workbook.addWorksheet('User Roles');

    // Define columns
    worksheet.columns = [
      { header: 'Name', key: 'name' },
      { header: 'Email', key: 'email' },
      { header: 'Chapter', key: 'chapter' },
      { header: 'Role', key: 'role' },
    ];

    // Style header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD3D3D3' }
    };

    // Freeze top row
    worksheet.views = [
      { state: 'frozen', ySplit: 1 }
    ];

    // Add auto-filter to header row
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: 4 }
    };

    // Add data rows
    this.filteredData.forEach(user => {
      worksheet.addRow({
        name: user.User?.name || '',
        email: user.User?.email || '',
        chapter: this.state.chapters.find(c => Number(c.id) === Number(user.Chapter?.id))?.location || '',
        role: user.Role?.name || '',
      });
    });

    // Auto-size columns based on content
    worksheet.columns.forEach(column => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, cell => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    // Generate filename with timestamp (YYYY-MM-DD_HHMMSS)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const filename = `clear_user_roles_${year}-${month}-${day}_${hours}${minutes}${seconds}.xlsx`;

    // Write to buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  handleChange = async (oldValue, newValue, row, column) => {
    if (!this.state.canUpdate) {
      window.alert("You do not have permissions to edit");
      return;
    }
    
    // Don't save if value hasn't actually changed (compare as numbers to handle type differences)
    if (Number(oldValue) === Number(newValue)) {
      return;
    }
    
    let user = { ...row };
    if (column.dataField === "Chapter.id") {
      user.chapter_id = newValue;
    } else if (column.dataField === "Role.id") {
      user.role_id = newValue;
    }
    
    try {
      // Pass info for logging
      await this.saveUser(user, {
        sourceUser: this.props.currentUser.id,
        column: column.dataField,
        oldValue,
        newValue,
      });

      // Update only the specific user in the state
      let users = this.state.users.map(u => {
        if (u.id === row.id) {
          return {
            ...u,
            Chapter: column.dataField === "Chapter.id" 
              ? { ...u.Chapter, id: newValue }
              : u.Chapter,
            Role: column.dataField === "Role.id"
              ? { ...u.Role, id: newValue }
              : u.Role,
            chapter_id: column.dataField === "Chapter.id" ? newValue : u.chapter_id,
            role_id: column.dataField === "Role.id" ? newValue : u.role_id,
          };
        }
        return u;
      });
      this.setState({ users });

      // Show success message
      const fieldName = column.dataField === "Chapter.id" ? "chapter" : "role";
      const oldLabel = column.dataField === "Chapter.id" 
        ? this.state.chapters.find(c => Number(c.id) === Number(oldValue))?.location 
        : this.state.roles.find(r => Number(r.id) === Number(oldValue))?.name;
      const newLabel = column.dataField === "Chapter.id" 
        ? this.state.chapters.find(c => Number(c.id) === Number(newValue))?.location 
        : this.state.roles.find(r => Number(r.id) === Number(newValue))?.name;
      
      toast.success(
        `Successfully updated ${row.User.name}'s ${fieldName} from ${oldLabel} to ${newLabel}.`
      );
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error("Failed to update user. Please try again.");
    }
  };

  render = () => {
    if (
      this.props.currentUser.Role.permission_level < PermissionLevel.Committee
    ) {
      return <Unauthorized />;
    }

    // a "fake permission" when the permission level is based on PERMISSION_LEVEL in development .env
    // a "fake permission" cannot login as another user, since they have no user id.
    const isFakePermission = this.props.currentUser.Role.name == 'user'

    const canShowLoginAs = this.canShowLoginAs()
    const canLoginAs = !isFakePermission && this.state.simUser?.length && this.state.simUser[0].id
    return (
      <AdminPane as="div" width="full" className="admin manage-users-container">
        <PrismTabs
          ref={this.tabsRef}
          setAriaLabel="User management"
          className="clr:block"
          onPrismChange={(e) => {
            const detail = e.detail || {};
            if (detail.action === 'visible' && detail.tabId) {
              this.setState({ activeTab: detail.tabId });
            }
          }}
        >
        {/*
          Panels render eagerly, which matches what this pane already did when all three
          sections were stacked on one screen. Nothing here fetches on mount — the pane's
          own componentDidMount loads everything once — so there is no request to defer.
        */}
        <PrismTab tab={panelId('roles')} id={panelId('roles')}>
        <div className="manage-users-section">
      <div className="clr:flex clr:justify-between clr:items-center clr:mb-4">
        <h3 className="clr:mb-0">User Roles</h3>
        <div>
          <PrismButton
            type="button"
            tone="success"
            size="sm"
            className={!this.state.canUpdate ? "hidden" : ""}
            disabled={this.state.loading || !this.state.users.length}
            onPrismClick={this.exportUsers}
            style={{ marginRight: '2rem' }}
          >
            Export UAR
          </PrismButton>
          {/* The previous hand-rolled backgroundColor/color override existed only to force
              AA contrast that Bootstrap's palette missed in dark mode. Prism's tone tokens
              carry accessible foreground pairings themselves, so the override is dropped. */}
          <PrismButton
            type="button"
            tone="secondary"
            size="sm"
            className={!this.state.canUpdate ? "hidden" : ""}
            disabled={this.state.loading || !this.filteredData || !this.filteredData.length}
            onPrismClick={this.exportCurrentView}
          >
            Export Current View
          </PrismButton>
        </div>
      </div>
      
      <PrismBanner tone="info" layout="inline" className="clr:mb-4">
        Only Admins and Officers can manage user roles. This table shows the current year's program members, excluding users with the basic "User" role.
      </PrismBanner>

      {this.state.loading ? (
        <PrismLoading as="spinner" container="inline" />
      ) : (
        <>
          <div className="manage-users-toolbar-filters">
            <div className="manage-users-toolbar-filter">
              <PrismInputText
                label="Name"
                size="sm"
                placeholder="Filter by name..."
                value={this.state.nameFilter}
                onPrismInput={(e) => {
                  const next = String(e.detail?.value ?? e.detail ?? '');
                  if (next === this.state.nameFilter) return;
                  this.setState({ nameFilter: next });
                }}
              />
            </div>
            <div className="manage-users-toolbar-filter">
              <PrismSelect
                label="Chapter"
                size="sm"
                value={this.state.chapterFilter}
                options={[
                  { value: '', display: 'All Chapters' },
                  ...this.state.chapters.map((c) => ({
                    value: String(c.id),
                    display: c.location,
                  })),
                ]}
                onPrismChange={(e) => {
                  const next = String(e.detail?.value ?? e.detail ?? '');
                  if (next === this.state.chapterFilter) return;
                  this.setState({ chapterFilter: next });
                }}
              />
            </div>
            <div className="manage-users-toolbar-filter">
              <PrismSelect
                label="Role"
                size="sm"
                value={this.state.roleFilter}
                options={[
                  { value: '', display: 'All Roles' },
                  ...this.state.roles
                    .filter((r) => Number(r.id) !== 4)
                    .map((r) => ({ value: String(r.id), display: r.name })),
                ]}
                onPrismChange={(e) => {
                  const next = String(e.detail?.value ?? e.detail ?? '');
                  if (next === this.state.roleFilter) return;
                  this.setState({ roleFilter: next });
                }}
              />
            </div>
            <div className="manage-users-toolbar-filter manage-users-toolbar-clear">
              <PrismButton
                tone="secondary"
                display="outline"
                size="sm"
                onPrismClick={this.handleClearAllFilters}
                title="Clear all filters"
              >
                <img src={clearLogoSrc} alt="CLEAR" style={{ height: '12px' }} />
                {' '}All Filters
              </PrismButton>
            </div>
          </div>

          <PrismDataGrid
            className="admin-tbl"
            keyField="id"
            caption="Program members"
            data={this.getVisibleUsers()}
            columns={this.getColumns()}
            emptyMessage="No users match the current filters."
            onValidationError={(message) => toast.error(message)}
            onFilteredRowsChange={this.handleFilteredRowsChange}
          />
        </>
      )}
        </div>
        </PrismTab>

        <PrismTab tab={panelId('add')} id={panelId('add')}>
        <div className="manage-users-section">
          <div className={!this.state.canUpdate ? "hidden" : ""}>
            <PrismBanner tone="info" layout="inline" className="clr:mb-4">
              To add a member to this year's program, select a name first, then choose their chapter and role. Members already in the program can have their roles edited in the table below.
            </PrismBanner>

            {this.state.saving ? (
              <p>Adding Role...</p>
            ) : (
              <div>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="manage-users-add-row">
                    <div className="manage-users-add-user">
                      <UserTypeahead
                        id="user-name"
                        label="1. Name"
                        labelKey="name"
                        options={this.state.allUsers.filter((u) => {
                          return (
                            this.state.users.findIndex(
                              (v) => u.id === v.user_id
                            ) === -1
                          );
                        })}
                        selected={this.state.selUser || []}
                        placeholder="Enter Name..."
                        onChange={(selUser) => {
                          // Reset downstream fields when user changes
                          this.setState({ 
                            selUser,
                            selChapter: -1,
                            selRole: -1
                          });
                        }}
                      />
                    </div>
                    <div className="manage-users-add-chapter">
                      <PrismSelect
                        label="2. Chapter"
                        value={String(this.state.selChapter)}
                        options={[
                          { value: '-1', display: 'Select Chapter...' },
                          ...this.state.chapters.map((c) => ({
                            value: String(c.id),
                            display: c.location,
                          })),
                        ]}
                        onPrismChange={(e) => {
                          const next = String(e.detail?.value ?? e.detail ?? '');
                          // Ignore echo events fired on programmatic value set
                          if (next === String(this.state.selChapter)) return;
                          // Reset role when chapter changes
                          this.setState({ selChapter: next, selRole: -1 });
                        }}
                        disabled={!this.state.selUser || !this.state.selUser.length}
                      />
                    </div>
                    <div className="manage-users-add-role">
                      <PrismSelect
                        label="3. Role"
                        value={String(this.state.selRole)}
                        options={[
                          { value: '-1', display: 'Select Role...' },
                          ...this.state.roles
                            .filter((r) => Number(r.id) !== 3 && Number(r.id) !== 4)
                            .map((role) => ({
                              value: String(role.id),
                              display: role.name,
                            })),
                        ]}
                        onPrismChange={(e) => {
                          const next = String(e.detail?.value ?? e.detail ?? '');
                          if (next === String(this.state.selRole)) return;
                          this.setState({ selRole: next });
                        }}
                        disabled={!this.state.selUser || !this.state.selUser.length || !this.state.selChapter || this.state.selChapter === -1 || this.state.selChapter === "-1"}
                      />
                    </div>
                    <div className="manage-users-add-submit">
                      <PrismButton
                        onPrismClick={this.addUser}
                        disabled={!this.state.selUser || !this.state.selUser.length || !this.state.selChapter || this.state.selChapter === -1 || this.state.selChapter === "-1" || !this.state.selRole || this.state.selRole === -1 || this.state.selRole === "-1"}
                      >
                        Add User
                      </PrismButton>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
        </PrismTab>

        {canShowLoginAs ? (
        <PrismTab tab={panelId('impersonation')} id={panelId('impersonation')}>
        <div className="manage-users-section">
        <PrismBanner tone="caution" layout="inline" className="clr:mb-4">
          {isFakePermission ? (
            <p className="clr:mb-0">You'll need to add yourself as an Admin or Officer before you can impersonate another user.</p>
          ) : (
            <>
              <strong>Important:</strong> When impersonating another user, any changes you make are <strong>permanent</strong> and recorded as if that user made them. When finished, select <strong>End Impersonation</strong> from the user menu.
            </>
          )}
        </PrismBanner>
        {!isFakePermission && (
          /* The eligibility rule used to surface only as a failure, and before that not at
             all — the request silently proceeded as the original user. The list is now
             filtered to eligible users, so the rule has to be explained here or an absent
             name looks like a bug rather than the expected roll-over. */
          <PrismBanner tone="info" layout="inline" className="clr:mb-4">
            <>
              <strong>Who you can impersonate:</strong> only users who hold a role for the
              current program year ({new Date().getFullYear()}).{' '}
              {this.state.simCandidates.length > 0
                ? `${this.state.simCandidates.length} ${this.state.simCandidates.length === 1 ? 'user is' : 'users are'} eligible right now.`
                : 'No users are eligible right now.'}{' '}
              The roster rolls over each January, so last year's participants will not appear
              until they are given a role for this year — add them on the{' '}
              <strong>Add a User Role</strong> tab first, then come back here.
            </>
          </PrismBanner>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5em 0' }}>
          <div style={{ width: '300px' }}>
            <UserTypeahead
              id="sim-user-name"
              label="User to simulate"
              hideLabel
              labelKey="name"
              options={this.state.simCandidates}
              selected={this.state.simUser || []}
              placeholder="Enter Name..."
              onChange={(simUser) => this.setState({ simUser })}
            />
          </div>
          {this.state.simUser && this.state.simUser.length > 0 && (
            <PrismButton
              tone="secondary"
              display="ghost-icon"
              spacing="compact"
              onPrismClick={() => this.setState({ simUser: null })}
              title="Clear selection"
              setAriaLabel="Clear selection"
            >
              {/* Default slot + explicit size; see ConfirmDeleteButton. */}
              <FontAwesomeIcon icon={faXmark} style={{ width: "1em", height: "1em" }} />
            </PrismButton>
          )}
          <PrismButton onPrismClick={()=>this.props.loginAsUser(this.state.simUser[0].id)} disabled={!canLoginAs}>
            Impersonate User
          </PrismButton>
        </div>
        </div>
        </PrismTab>
        ) : undefined }
        </PrismTabs>
      </AdminPane>
    );
  };
}

export default ManageUsers;
